// The lines printed under a card's name on the collection receipt. TKT-2026-105380.
//
// The receipt printed the card name and its condition/printing and nothing else, so a
// store handing a shopper their pickup slip could not tell which set a card came from.
// A shopper buying four different printings of the same card got four rows that read
// identically apart from the condition.
//
// Every cart item already carries the set: Dashboard.js puts `groupName` and
// `setName` on it when the card is added, and CartPage.js sends both to the order API
// at checkout. Nothing needed fetching, the receipt just never printed it.
//
// `products` has no setName of its own, so `setName` is the kiosk's alias for
// `groupName` and the two agree. Preferring groupName keeps the receipt on the same
// field the seller's inventory grid and platform pushes use, rather than the
// `groups.name` the catalog picker reads (TKT-2026-105028, TKT-2026-105254).

import { gradedLineLabel, isGradedVariant } from './buylistGraded';

/**
 * The set name to print for a cart item, or '' when the item has none
 * (custom items, sealed rows added without catalog data).
 *
 * @param {Object} item a cart item
 * @returns {string}
 */
export const resolveCartItemSetName = (item) => {
  if (!item || typeof item !== 'object') return '';
  for (const value of [item.groupName, item.setName]) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

/**
 * The muted detail lines that print under the card name, in order. Each is printed
 * on its own line because a set name is far wider than a 280px thermal receipt.
 *
 * @param {Object} item a cart item
 * @returns {string[]} zero to two lines, three for a slab with a cert
 */
export const buildReceiptItemLines = (item) => {
  const lines = [];
  const setName = resolveCartItemSetName(item);
  if (setName) lines.push(setName);

  // A slab reads as its grader and grade ('PSA Grade 10 / Normal'), and its cert
  // goes on a line of its own so the store can match the slab in hand to the
  // slip. TKT-2026-107322
  const variant = gradedLineLabel(item?.variant) || [item?.variant?.condAbbr, item?.variant?.printing]
    .filter(Boolean)
    .join(' / ');
  if (variant) lines.push(variant);

  if (isGradedVariant(item?.variant) && item.variant.certNumber) {
    lines.push(`Cert ${item.variant.certNumber}`);
  }

  return lines;
};

export default buildReceiptItemLines;
